/** Billing page — plans, checkout, portal, invoices. */
import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, ExternalLink, Loader2, Rocket, Sparkles } from "lucide-react";

import { useCurrentWorkspace } from "@/entities/workspace";
import {
  getSubscription, listInvoices, listPlans, openPortal, startCheckout,
  type Plan, type PlanCode,
} from "@/entities/billing";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useState } from "react";

const cur = (v: number) => `$${v.toFixed(0)}`;

export default function BillingPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("session_id")) toast.success("Subscription created — welcome aboard.");
    if (params.get("canceled")) toast("Checkout canceled.");
  }, [params]);
  if (!workspace) {
    return <div data-testid="billing-empty" className="mx-auto max-w-2xl px-6 py-16 text-center">
      <h1 className="font-display text-3xl font-semibold">Billing</h1>
      <p className="mt-2 text-sm text-muted-foreground">Select a workspace to see billing.</p>
    </div>;
  }
  return (
    <div data-testid="billing-page" className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <header>
        <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-3 w-3" /> M10 · Live Stripe · Managed Payments
        </div>
        <h1 data-testid="billing-title" className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
          Plans & Billing
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Every capability is gated by your plan. Change tiers whenever — Stripe handles taxes and invoices.
        </p>
      </header>

      <Tabs defaultValue="plans" className="space-y-4">
        <TabsList>
          <TabsTrigger data-testid="tab-plans" value="plans">Plans</TabsTrigger>
          <TabsTrigger data-testid="tab-invoices" value="invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="space-y-4"><PlansPanel wsId={workspace.id} /></TabsContent>
        <TabsContent value="invoices" className="space-y-4"><InvoicesPanel wsId={workspace.id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function PlansPanel({ wsId }: { wsId: string }): JSX.Element {
  const plans = useQuery({ queryKey: ["billing", "plans"], queryFn: listPlans });
  const sub = useQuery({ queryKey: ["billing", "sub", wsId], queryFn: () => getSubscription(wsId) });
  const [interval, setInterval] = useState<"month" | "year">("month");

  const checkout = useMutation({
    mutationFn: (plan_code: PlanCode) => startCheckout(wsId, {
      plan_code, interval, origin_url: window.location.origin,
    }),
    onSuccess: (r) => { window.location.href = r.checkout_url; },
    onError: (e: Error) => toast.error(e.message),
  });

  const portal = useMutation({
    mutationFn: () => openPortal(wsId, window.location.href),
    onSuccess: (r) => { window.location.href = r.portal_url; },
    onError: (e: Error) => toast.error(e.message),
  });

  const active = sub.data?.plan_code ?? "free";

  return (
    <div className="space-y-4">
      <Card className="border-border/60 bg-card/70">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Current plan</p>
            <p className="mt-0.5 font-display text-xl font-semibold capitalize" data-testid="current-plan">
              {sub.data?.plan.display_name ?? "—"}
              {sub.data?.active ? (
                <Badge className="ml-2 bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200">active</Badge>
              ) : (
                <Badge className="ml-2 bg-zinc-200 text-zinc-800 dark:bg-zinc-900 dark:text-zinc-200">{sub.data?.status ?? "none"}</Badge>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border/60 bg-background/70 p-0.5 text-xs">
              <button data-testid="interval-month" onClick={() => setInterval("month")}
                      className={`rounded px-3 py-1 ${interval === "month" ? "bg-foreground text-background" : "text-muted-foreground"}`}>
                Monthly
              </button>
              <button data-testid="interval-year" onClick={() => setInterval("year")}
                      className={`rounded px-3 py-1 ${interval === "year" ? "bg-foreground text-background" : "text-muted-foreground"}`}>
                Annual · 2 mo free
              </button>
            </div>
            {sub.data?.active ? (
              <Button variant="outline" size="sm" onClick={() => portal.mutate()} data-testid="portal-button">
                {portal.isPending ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <CreditCard className="mr-2 h-3 w-3" />}
                Manage in Stripe
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {plans.isLoading ? Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-96 w-full" />
        )) : plans.data?.map((p) => (
          <PlanCard key={p.plan_code} plan={p} interval={interval} active={active === p.plan_code}
                    onSelect={() => checkout.mutate(p.plan_code)}
                    busy={checkout.isPending} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({
  plan, interval, active, onSelect, busy,
}: {
  plan: Plan; interval: "month" | "year"; active: boolean; onSelect: () => void; busy: boolean;
}): JSX.Element {
  const price = interval === "year" && plan.annual_price_usd !== null
    ? plan.annual_price_usd / 12
    : plan.monthly_price_usd;
  const suffix = interval === "year" ? " /mo · billed annually" : " /mo";
  return (
    <Card data-testid={`plan-card-${plan.plan_code}`} className={`border-border/60 bg-card/70 ${active ? "ring-2 ring-indigo-400/60" : ""}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between font-display text-xl">
          <span>{plan.display_name}</span>
          {plan.recommended ? (
            <Badge className="bg-indigo-100 text-indigo-900 dark:bg-indigo-950/60 dark:text-indigo-200">Most popular</Badge>
          ) : null}
        </CardTitle>
        <CardDescription>{plan.is_contact_sales ? "Custom deployment, capacity, and support." : plan.tagline}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {plan.is_contact_sales ? (
          <div className="min-h-[3rem]">
            <span className="font-display text-4xl font-semibold tabular-nums">From {cur(plan.monthly_price_usd ?? 0)}</span>
            <span className="text-xs text-muted-foreground"> /mo</span>
          </div>
        ) : (
          <div className="min-h-[3rem]">
            <span className="font-display text-4xl font-semibold tabular-nums">{cur(price ?? 0)}</span>
            <span className="text-xs text-muted-foreground">{suffix}</span>
            {interval === "year" && plan.annual_savings_usd > 0 ? (
              <p className="mt-1 text-xs text-emerald-500">save {cur(plan.annual_savings_usd)} vs monthly</p>
            ) : null}
          </div>
        )}
        <ul className="space-y-1 text-xs text-muted-foreground">
          <PlanRow label="Workspaces" value={limit(plan.limits.workspaces)} />
          <PlanRow label="Team seats" value={limit(plan.limits.team_seats)} />
          <PlanRow label="Text generations /mo" value={limit(plan.limits.text_generations)} />
          <PlanRow label="Premium images /mo" value={limit(plan.limits.premium_images_per_month)} />
          <PlanRow label="Included video seconds /mo" value={limit(plan.limits.video_seconds_per_month)} />
          <PlanRow label="Voice minutes /mo" value={limit(plan.limits.voice_minutes_per_month)} />
          <PlanRow label="Publishing channels" value={limit(plan.limits.publishing_channels)} />
            {plan.capabilities.includes("autopilot") && (
              <PlanRow label="Autopilot access" value={plan.limits.autopilot_max_level === "executive" ? "All trust levels" : title(plan.limits.autopilot_max_level)} />
            )}
          <PlanRow label="Analytics" ok={plan.capabilities.includes("analytics")} />
          <PlanRow label="Advanced analytics" ok={plan.capabilities.includes("analytics_advanced")} />
        </ul>
        {plan.is_contact_sales ? (
          <Button
            variant="outline"
            className="w-full"
            asChild
            data-testid={`plan-contact-${plan.plan_code}`}
          >
            <a href="mailto:enterprise@orkexra.com?subject=Orkexra Enterprise inquiry">
              Contact sales <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
        ) : (
          <Button
            onClick={onSelect}
            disabled={busy || active || plan.plan_code === "free"}
            data-testid={`plan-select-${plan.plan_code}`}
            className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> :
              active ? <Check className="mr-2 h-4 w-4" /> : <Rocket className="mr-2 h-4 w-4" />}
            {active ? "Current plan" : plan.plan_code === "free" ? "Included" : `Choose ${plan.display_name}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

const limit = (value: number | null): string => value === null ? "Unlimited" : String(value);
const title = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

function PlanRow({ label, value, ok }: { label: string; value?: string; ok?: boolean }): JSX.Element {
  return (
    <li className="flex items-center justify-between">
      <span>{label}</span>
      {value !== undefined ? (
        <span className="tabular-nums text-foreground">{value}</span>
      ) : (
        <span className={ok ? "text-emerald-500" : "text-muted-foreground/50"}>
          {ok ? "✓" : "—"}
        </span>
      )}
    </li>
  );
}

function InvoicesPanel({ wsId }: { wsId: string }): JSX.Element {
  const q = useQuery({ queryKey: ["billing", "invoices", wsId], queryFn: () => listInvoices(wsId) });
  return (
    <Card data-testid="invoices-card" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg">Invoice history</CardTitle>
        <CardDescription>Every subscription payment is recorded via Stripe webhook.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? <Skeleton className="h-24 w-full" /> :
          q.data && q.data.length > 0 ? (
            <div className="space-y-1 text-xs">
              {q.data.map((inv) => (
                <div key={inv._id} data-testid={`invoice-row-${inv._id}`}
                     className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/50 bg-background/60 p-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{inv.number ?? inv.stripe_invoice_id.slice(-8)}</span>
                    <Badge variant="outline" className="font-mono text-[10px] uppercase">{inv.status}</Badge>
                  </div>
                  <div className="flex items-center gap-3 font-mono">
                    <span>{cur(inv.amount_paid_cents / 100)}</span>
                    {inv.hosted_invoice_url ? (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                         className="text-indigo-500 hover:underline">view</a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-border/50 bg-background/40 p-6 text-center text-xs text-muted-foreground">
              No invoices yet. When you subscribe, Stripe will send them here.
            </p>
          )}
      </CardContent>
    </Card>
  );
}
